/**
 * Where an avatar picture is cut. One formula, used by the server (which cuts the stored pictures) and by the settings app
 * (which shows the live preview), so what the owner sees while adjusting is exactly what the chat shows.
 *
 * The picture is cut as a square. At zoom 1 the square is as large as the picture's shorter side; zoom 2 shows half of that
 * side, and so on. `x` and `y` say where the middle of the square sits in the picture (0 = left/top edge, 0.5 = middle,
 * 1 = right/bottom edge). The square never leaves the picture.
 */
export interface AvatarCrop {
  /** 1 (the whole shorter side) to 4. */
  zoom: number;
  /** Middle of the square, 0 to 1 across the picture. */
  x: number;
  /** Middle of the square, 0 to 1 down the picture. */
  y: number;
}

export const AVATAR_ZOOM_MIN = 1;
export const AVATAR_ZOOM_MAX = 4;
export const DEFAULT_AVATAR_CROP: AvatarCrop = { zoom: 1, x: 0.5, y: 0.5 };

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

/** Anything that is not a sensible number falls back to the default; values are limited to their ranges. */
export function normalizeAvatarCrop(input: Partial<Record<keyof AvatarCrop, unknown>> | null | undefined): AvatarCrop {
  const num = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v)) ? Number(v) : d);
  return {
    zoom: clamp(num(input?.zoom, 1), AVATAR_ZOOM_MIN, AVATAR_ZOOM_MAX),
    x: clamp(num(input?.x, 0.5), 0, 1),
    y: clamp(num(input?.y, 0.5), 0, 1),
  };
}

/** The square to cut, in the picture's own pixels (whole numbers, inside the picture). */
export function avatarCropRect(width: number, height: number, crop: AvatarCrop): { left: number; top: number; size: number } {
  const c = normalizeAvatarCrop(crop);
  const size = Math.max(1, Math.min(Math.min(width, height), Math.round(Math.min(width, height) / c.zoom)));
  const left = Math.round(clamp(c.x * width - size / 2, 0, width - size));
  const top = Math.round(clamp(c.y * height - size / 2, 0, height - size));
  return { left, top, size };
}
